import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BiChevronDown } from 'react-icons/bi';
import { FaTooth } from 'react-icons/fa';
import { FaFileDownload } from 'react-icons/fa';
import { FiEye } from 'react-icons/fi';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import axios from 'axios';
import {
  getHistorialOdontogramaByPatientId,
  getHistorialOdontograma,
} from '../../redux/features/odontogramaSlice';
import HistorialTratamientosPdf from '../../pdf/HistorialTratamientosPdf';
import SelectedHistorialModal from '../../components/Modals/SelectedHistorialModal';
import { specificConfigurationDataUrl } from '../../redux/apiUrl';

function TipoTag({ tipo }) {
  if (!tipo) return null;
  const lower = tipo.toLowerCase();
  if (lower === 're consulta')
    return (
      <span className="text-xs font-medium text-subMain border border-subMain rounded-full px-2 py-0.5">
        {tipo}
      </span>
    );
  if (lower === 'control')
    return (
      <span className="text-xs font-medium text-textGray border border-border rounded-full px-2 py-0.5">
        {tipo}
      </span>
    );
  return (
    <span className="text-xs font-medium text-subMain border border-subMain rounded-full px-2 py-0.5">
      {tipo}
    </span>
  );
}

export default function HistorialTratamientos({ patientId, patient }) {
  const dispatch = useDispatch();
  const historial = useSelector(getHistorialOdontograma);
  const [abiertos, setAbiertos] = useState(new Set());
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [doctorData, setDoctorData] = useState(null);

  useEffect(() => {
    if (patientId) {
      dispatch(getHistorialOdontogramaByPatientId(patientId));
    }
  }, [patientId, dispatch]);

  useEffect(() => {
    axios.get(specificConfigurationDataUrl)
      .then((res) => setDoctorData(res.data))
      .catch(() => {});
  }, []);

  const toggle = (cita_id) => {
    setAbiertos((prev) => {
      const next = new Set(prev);
      if (next.has(cita_id)) next.delete(cita_id);
      else next.add(cita_id);
      return next;
    });
  };

  const handleImprimir = async (seleccionados) => {
    const nombre = patient
      ? `${patient.persona?.nombres ?? ''} ${patient.persona?.apellidos ?? ''}`.trim()
      : 'Paciente';
    const blob = await pdf(
      <HistorialTratamientosPdf
        nombre={nombre}
        historial={seleccionados}
        doctorData={doctorData}
      />
    ).toBlob();
    saveAs(blob, `Historial-${nombre}-${new Date().toLocaleDateString('en-GB')}.pdf`);
  };

  if (!historial || historial.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-textGray">
        <FaTooth className="text-4xl mb-3 opacity-20" />
        <p className="text-sm">No hay registros de tratamientos para este paciente</p>
      </div>
    );
  }

  return (
    <>
      {isOpenModal && (
        <SelectedHistorialModal
          isOpen={isOpenModal}
          closeModal={() => setIsOpenModal(false)}
          historial={historial}
          onImprimir={handleImprimir}
        />
      )}

      <div className="flex flex-col gap-2">
        {/* Botón descargar */}
        <div className="flex justify-end px-1 my-2">
          <button
            onClick={() => setIsOpenModal(true)}
            className="flex items-center gap-2 bg-subMain text-white text-xs rounded-lg px-3 py-1.5 hover:opacity-90 transition-opacity"
          >
            <FaFileDownload className="text-sm" />
            Descargar PDF
          </button>
        </div>

        <div className="overflow-y-auto pr-1" style={{ maxHeight: 'calc(70vh - 80px)' }}>
        {historial.map((cita) => (
          <div key={cita.cita_id} className="border border-border rounded-xl overflow-hidden">
            {/* Cabecera */}
            <button
              onClick={() => toggle(cita.cita_id)}
              className="w-full flex items-center justify-between px-4 py-3 bg-dry hover:bg-greyed transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="bg-subMain text-white w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0">
                  {cita.tipo_evento === 'Re Consulta'
                    ? <FiEye className="text-sm" />
                    : <FaTooth className="text-sm" />}
                </div>
                <div>
                  <p className="text-xs font-semibold text-main">{cita.fecha}</p>
                  <p className="text-xs text-textGray">
                    {cita.piezas.length} pieza{cita.piezas.length !== 1 ? 's' : ''} tratada{cita.piezas.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <TipoTag tipo={cita.tipo_evento} />
                <BiChevronDown
                  className={`text-textGray text-lg transition-transform ${
                    abiertos.has(cita.cita_id) ? 'rotate-180' : ''
                  }`}
                />
              </div>
            </button>

            {/* Piezas */}
            {abiertos.has(cita.cita_id) && (
              <div className="divide-y divide-border bg-white">
                {cita.piezas.map((pieza, idx) => (
                  <div key={idx} className="px-4 py-3 flex gap-4 items-start">
                    <div className="bg-text text-subMain text-xs font-bold w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      {pieza.diente}
                    </div>
                    <div className="flex flex-col gap-0.5 flex-1">
                      {pieza.diagnostico && (
                        <p className="text-xs text-main">
                          <span className="font-medium">Diagnóstico:</span>{' '}
                          {pieza.diagnostico}
                        </p>
                      )}
                      {pieza.tratamiento && (
                        <p className="text-xs text-main">
                          <span className="font-medium">Tratamiento:</span>{' '}
                          {pieza.tratamiento}
                        </p>
                      )}
                      {pieza.costo && (
                        <p className="text-xs text-textGray">
                          Costo: ${Number(pieza.costo).toLocaleString('es-CO')}
                        </p>
                      )}
                      {pieza.notas?.length > 0 && (
                        <div className="mt-1 flex flex-col gap-1 border-t border-border pt-1">
                          {pieza.notas.map((nota, nIdx) => (
                            <div key={nIdx} className="flex items-start gap-2">
                              <span className="text-xs text-textGray flex-shrink-0">{nota.fecha}</span>
                              <span
                                className={`text-xs flex-shrink-0 font-medium ${
                                  nota.estado === 'completado' ? 'text-green-600' : 'text-subMain'
                                }`}
                              >
                                {nota.estado === 'completado' ? 'Completado' : 'En avance'}
                              </span>
                              {nota.nota && (
                                <span className="text-xs text-main">{nota.nota}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        </div>
      </div>
    </>
  );
}
